import { WIZARD_STEP_COUNTS } from '@finn-no/mobility-item-insertion-shared';
import { useLingui } from '@lingui/react';
import { Trans } from '@lingui/react/macro';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, Button } from '@warp-ds/react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import type { LookupCfg } from '../../../types/config.js';
import { LookupWizardNavigation } from '../../components/vehicle-lookup/LookupWizardNavigation.js';
import {
  type CarInfoFormInputs,
  RegistrationNumberForm,
} from '../../components/vehicle-lookup/RegistrationNumberForm.js';
import {
  lookupCategoryMismatchButton,
  lookupCategoryMismatchMessage,
  lookupCategoryMismatchTitle,
  lookupRegNoPlaceholder,
  lookupTitle,
} from '../../helpers/lookup-messages.js';
import { redirectToItemInsertion } from '../../helpers/vehicle-lookup-redirect.js';
import { useLookupPulse } from '../../hooks/useLookupPulse.js';
import { createItemFromVehicleLookup } from '../../services/vehicle-item-creation.js';
import { lookupVehicle, VehicleCategoryMismatchError } from '../../services/vehicle-lookup.js';

type VehicleLookupPageProps = {
  config: LookupCfg;
};

export const VehicleLookupPage = ({ config }: VehicleLookupPageProps) => {
  const { i18n } = useLingui();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const queryClient = useQueryClient();
  const { itemId: routeItemId } = useParams<{ itemId?: string }>();
  const [error, setError] = useState<string>();
  const [alert, setAlert] = useState<null | 'CreateError' | 'CategoryMismatch'>(null);
  const [recommendedModel, setRecommendedModel] = useState<string | null>(null);
  const [currentRegnr, setCurrentRegnr] = useState<string | null>(null);

  // Use model from URL params if present, otherwise fall back to config.model
  const currentModel = params.get('model') || config.model;
  const { trackPageView, trackClick } = useLookupPulse(currentModel);

  // Determine if we're in the new flow (TAI-1899) - item already exists
  const effectiveItemId = routeItemId || config.itemId;
  const isNewFlow = Boolean(effectiveItemId);

  useEffect(() => {
    trackPageView();
  }, []);

  // Reset error and alert states when URL parameters change
  useEffect(() => {
    setError(undefined);
    setAlert(null);
    setRecommendedModel(null);
  }, [params]);

  // Skip mutation - creates item (legacy) or navigates directly (new flow)
  const skipMutation = useMutation({
    mutationFn: async () => {
      setAlert(null);
      if (isNewFlow && effectiveItemId) {
        // New flow: item already exists, just return null (we'll navigate in onSuccess)
        return null;
      }
      // Legacy flow: create item first
      return createItemFromVehicleLookup(currentModel, {}, params.get('orgId') ?? undefined);
    },
    onSuccess: (itemData) => {
      const orgId = params.get('orgId') ?? undefined;

      // Handle redirect using shared helper
      // Skip always goes to root page (not step 2) since user hasn't completed lookup
      redirectToItemInsertion({
        itemData,
        effectiveItemId,
        orgId,
        config: {
          root: config.root,
          isBackNavigationEnabled: config.isBackNavigationEnabled,
          isWizardEnabled: config.isWizardEnabled,
          model: currentModel,
        },
      });
    },
    onError: () => {
      setAlert('CreateError');
    },
  });

  const handleLookup = async ({ regnr }: CarInfoFormInputs) => {
    try {
      setError(undefined);
      setAlert(null);
      setRecommendedModel(null);
      setCurrentRegnr(regnr);
      // This will check if the car exists before navigating (and the data will also be cached - query key on both pages needs to match)
      const result = await queryClient.fetchQuery({
        queryKey: ['regnr', regnr],
        queryFn: () =>
          lookupVehicle({
            regnr,
            csrfToken: config.csrfToken,
            root: config.root,
            model: currentModel,
            isCategoryMismatchEnabled: config.isCategoryMismatchEnabled,
          }),
      });

      if (!result) {
        setError(
          i18n.t({
            id: 'mobility-item-insertion.lookup.vehicle.not-found',
            comment: 'message shows to users when looking up a vehicle by registration number yields no results',
            message: 'Could not find the registration number',
          }),
        );
        return;
      }

      const orgId = params.get('orgId');
      // Use item-scoped route in new flow (TAI-1899)
      const basePath = effectiveItemId ? `/item/${effectiveItemId}/lookup` : '/lookup';
      const url = orgId
        ? `${basePath}/${regnr}?model=${currentModel}&orgId=${orgId}`
        : `${basePath}/${regnr}?model=${currentModel}`;

      navigate(url);
    } catch (error) {
      if (error instanceof VehicleCategoryMismatchError) {
        // Category mismatch - show warning alert
        setRecommendedModel(error.recommendedAdModel);
        setAlert('CategoryMismatch');
        setError(
          i18n.t({
            id: 'mobility-item-insertion.lookup.vehicle.not-found',
            comment: 'message shows to users when looking up a vehicle by registration number yields no results',
            message: 'Could not find the registration number',
          }),
        );
      } else {
        // Generic error
        setError(
          i18n.t({
            id: 'mobility-item-insertion.vehicle-lookup-page.lookup-error',
            message:
              'We were unable to lookup the registration number due to an error. Please try again later or specify the car manually.',
          }),
        );
      }
    }
  };

  const buildRedirectUrl = (model: string, regnr: string): string => {
    const orgId = params.get('orgId');
    const queryParams = new URLSearchParams();
    queryParams.set('model', model);
    queryParams.set('regnr', regnr);
    if (orgId) {
      queryParams.set('orgId', orgId);
    }

    // Use item-scoped route in new flow (TAI-1899)
    const basePath = effectiveItemId ? `${config.root}/item/${effectiveItemId}/lookup` : `${config.root}/lookup`;
    return `${basePath}?${queryParams.toString()}`;
  };

  const handleRedirectToRecommendedModel = () => {
    if (!recommendedModel || !currentRegnr) return;
    trackClick({
      intent: 'Register',
      name: 'Category mismatch redirect',
      object: {
        elementType: 'Button',
      },
    });
    // Use full page reload to get updated server-side config (e.g., isWizardEnabled may change based on model)
    window.location.assign(buildRedirectUrl(recommendedModel, currentRegnr));
  };

  const displayStep = 1;
  const stepCount = WIZARD_STEP_COUNTS[config.model as keyof typeof WIZARD_STEP_COUNTS] ?? 0;
  const displayTotal = stepCount + 1; // +1 for vehicle-lookup step

  return (
    <main className="!mb-32 max-w-[700] mx-auto">
      <div className="flex flex-col mb-32">
        {config.isWizardEnabled ? (
          <header>
            <div className="t4 mb-4">
              <Trans
                id="mobility-item-insertion.form.wizard.progress"
                comment="Indicates the current step and total steps in the wizard"
              >
                Step {displayStep} of {displayTotal}
              </Trans>
            </div>
            <h1 className="mb-16">{lookupTitle(currentModel)}</h1>
          </header>
        ) : (
          <h1>{lookupTitle(currentModel)}</h1>
        )}
        <p>
          <Trans
            id="mobility-item-insertion.vehicle-lookup-page.subtitle"
            comment="Subtitle on the vehicle info page. Language should be neutral regarding the vehicle type."
          >
            Use the registration number to fetch vehicle information from the vehicle registry.
          </Trans>
        </p>
      </div>

      <RegistrationNumberForm
        onSubmit={handleLookup}
        error={error}
        placeholder={lookupRegNoPlaceholder(currentModel)}
        defaultValue={config.item?.data.registration_number || params.get('regnr') || undefined}
      />

      <Alert type="warning" show={alert === 'CategoryMismatch'}>
        <h3 className="t4">{lookupCategoryMismatchTitle(recommendedModel || '')}</h3>
        <p>{lookupCategoryMismatchMessage()}</p>
        {recommendedModel && (
          <Button onClick={handleRedirectToRecommendedModel} className="mt-16" data-testid="change-category-button">
            {lookupCategoryMismatchButton(recommendedModel)}
          </Button>
        )}
      </Alert>
      {!config.item?.data.vehicle_lookup?.variant_id && (
        <Button
          onClick={() => {
            trackClick({
              intent: 'Register',
              name: 'Car lookup without regnumber',
              object: { elementType: 'Link' },
            });
            skipMutation.mutate();
          }}
          link
          data-testid={'skip-lookup'}
          className="mt-16"
        >
          <Trans
            id="mobility-item-insertion.vehicle-lookup-page.no-reg-nr.button"
            comment="Button text for users who want to add all information themselves or do not have the registration number"
          >
            I want to add all information myself / I don't have the registration number
          </Trans>
        </Button>
      )}
      <Alert type="negative" show={alert === 'CreateError'} className="mt-16">
        <h3 className="t5">
          <Trans
            id="mobility-item-insertion.vehicle-lookup-page.creating-ad-failed.title"
            comment="Shown if creating an ad directly fails"
          >
            Something went wrong
          </Trans>
        </h3>
        <p>
          <Trans
            id="mobility-item-insertion.vehicle-lookup-page.creating-ad-failed.text"
            comment="Shown if creating an ad directly fails"
          >
            Please refresh the page and try again.
          </Trans>
        </p>
      </Alert>
      {config.isWizardEnabled && (
        <LookupWizardNavigation
          variant="skip"
          onSkip={() => {
            skipMutation.mutate();
          }}
          displayStep={displayStep}
          displayTotal={displayTotal}
          isLoading={skipMutation.isPending}
          navigationButtons={false}
        />
      )}
    </main>
  );
};
